import React from 'react';

import styled from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';
import renderRichText from '../../utils/render-rich-text';
import { Container, colors, media, Label, Heading, Text, TitleSet } from '../../ui-components';
import { getImage } from 'gatsby-plugin-image';

/**
 * @component renderHeroSection
 * 
 * @param {string} pageTitle
 * @param {string} heroSectionBody
 * @param {string} heroSectionLabel
 * @param {array} heroSectionImages
 * @param {string} level
 * 
 * @returns {JSX}
 */
const renderHeroSection = (pageTitle, heroSectionBody, heroSectionLabel, heroSectionImages, level) => {
  if (pageTitle && heroSectionBody && heroSectionLabel && heroSectionImages) {
    const renderStaticSubtitle = body => {
      const paragraphs = body.split('\n');
      return paragraphs.map((paragraph, index) => {
        return (
          <HeroSubtitle key={index} size={'large'}>
            {paragraph}
          </HeroSubtitle>
        );
      });
    };

    let subtitle = heroSectionBody?.raw ? (
      <HeroSubtitle>{renderRichText(heroSectionBody)}</HeroSubtitle>
    ) : (
      renderStaticSubtitle(heroSectionBody)
    );

    const image0 = getImage(heroSectionImages[0]);
    const image1 = getImage(heroSectionImages[1]);
    const image2 = getImage(heroSectionImages[2]);

    const renderDetailHeroSection = () => {
      return (
        <DetailHeroSection level={level}>
          <HeroImageSection level={level}>
            {image0 && <HeroImageTop
              loading="eager"
              fetchpriority="high"
              level={level}
              image={image0}
              alt={`${pageTitle} Hero Image 1`}
            />}
            <HeroImageBottomSection level={level}>
              {image1 && <HeroImageBottom
                loading="eager"
                fetchpriority="high"
                level={level}
                image={image1}
                alt={`${pageTitle} Hero Image 2`}
              />}
              {image2 && <HeroImageBottom
                loading="eager"
                fetchpriority="high"
                level={level}
                image={image2}
                alt={`${pageTitle} Hero Image 3`}
              />}
            </HeroImageBottomSection>
          </HeroImageSection>
          <DetailHeroTitleSection isPressAndMedia={level === 'press-and-media'}>
            <Label>{heroSectionLabel}</Label>
            <Heading level={1}>{pageTitle}</Heading>
            <HeroSubtitle size={'large'}>{subtitle}</HeroSubtitle>
          </DetailHeroTitleSection>
        </DetailHeroSection>
      );
    };

    const renderCoreHeroSection = () => {
      const image0 = getImage(heroSectionImages[0]);
      const image1 = getImage(heroSectionImages[1]);

      return (
        <CoreHeroSection>
          <HeroImageSection level={level}>
            <HeroImageBottomSection level={level}>
              {image0 && <HeroImageBottom level={level} image={image0} alt={`${pageTitle} Hero Image 1`}/>}
              {image1 && <HeroImageBottom level={level} image={image1} alt={`${pageTitle} Hero Image 2`}/>}
            </HeroImageBottomSection>
          </HeroImageSection>
          <CoreHeroTitleSection>
            <TitleSet label={heroSectionLabel} title={pageTitle} bodyText={subtitle} alignLeft />
          </CoreHeroTitleSection>
        </CoreHeroSection>
      );
    };

    const renderOverviewHeroSection = () => {
      const image = getImage(heroSectionImages);

      return (
        <OverviewHeroSection>
          <HeroImageSection level={level}>
            {image && <HeroImageTop level={level} image={getImage(heroSectionImages)} alt={`${pageTitle} Hero Image`}/>}
          </HeroImageSection>
          <OverviewHeroTitleSection>
            <Heading level={1}>{pageTitle}</Heading>
            <HeroSubtitle size={'large'}>{subtitle}</HeroSubtitle>
          </OverviewHeroTitleSection>
        </OverviewHeroSection>
      );
    };

    switch (level) {
      case 'detail':
      case 'press-and-media':
        return renderDetailHeroSection();
      case 'core':
        return renderCoreHeroSection();
      case 'overview':
        return renderOverviewHeroSection();
    }
  }
};

const DetailHeroSection = styled(Container)`
  padding: 0;
  margin-bottom: ${({ isPressAndMedia }) => (isPressAndMedia ? '-308px' : '-264px;')};

  @media screen and (max-width: ${media.mediumTop}) {
    margin-bottom: 0;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    margin-bottom: 60px;
  }
`;

const CoreHeroSection = styled(Container)`
  padding-top: 40px;
  padding-bottom: 0px;
  margin-bottom: 60px; //to make up for the negative 60 top positioning so we can get 120 difference

  @media screen and (max-width: ${media.mediumTop}) {
    padding-top: 12px;
    padding-left: 0px;
    padding-right: 0px;
    margin-bottom: 0px;
  }
  @media screen and (max-width: ${media.mediumBottom}) {
    margin-bottom: 60px;
  }
`;

const OverviewHeroSection = styled(Container)`
  padding: 0px;
  margin-bottom: -70px; //to make up for the -190 top positioning so we can have 120px difference

  @media screen and (max-width: ${media.mediumTop}) {
    margin-bottom: 0;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    margin-bottom: 60px;
  }
`;

const DetailHeroTitleSection = styled.div`
  max-width: 845px;
  padding: 60px 80px;
  background-color: ${colors.pureWhite};
  height: fit-content;
  position: relative;
  top: ${({ isPressAndMedia }) => (isPressAndMedia ? '-300px' : '-384px;')};

  @media screen and (max-width: ${media.mediumTop}) {
    display: flex;
    width: 75%;
    padding: 40px 64px;
    flex-direction: column;
    align-items: flex-start;
    top: -60px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column;
    position: initial;
    padding: 28px 20px;
    width: 100%;
    max-width: 100%;
  }
`;

const CoreHeroTitleSection = styled.div`
  max-width: 845px;
  padding: 60px 80px;
  background-color: ${colors.pureWhite};
  height: fit-content;
  position: relative;
  top: -60px;

  @media screen and (max-width: ${media.mediumTop}) {
    display: flex;
    width: 75%;
    padding: 40px 64px;
    flex-direction: column;
    align-items: flex-start;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column;
    position: initial;
    padding: 28px 20px;
    width: 100%;
    max-width: 100%;
  }
`;

const OverviewHeroTitleSection = styled.div`
  width: 732px;
  height: 260px;
  padding: 60px 80px;
  background-color: ${colors.pureWhite};
  height: fit-content;
  position: relative;
  top: -190px;

  @media screen and (max-width: ${media.mediumTop}) {
    padding: 40px 64px;
    width: 75%;
    top: -60px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 100%;
    height: auto;
    flex-direction: column;
    position: initial;
    padding: 28px 20px;
    margin-top: 12px;
  }
`;

const HeroSubtitle = styled(Text)`
  margin-top: 12px;
`;

const HeroImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media screen and (max-width: ${media.mediumBottom}) {
    align-items: ${({ level }) => (level === 'core' ? 'flex-start' : '')};
    overflow-x: hidden;
  }
`;

const HeroImageTop = styled(GatsbyImage)`
  max-height: 450px;
  max-width: 828px;

  @media screen and (max-width: ${media.mediumTop}) {
    width: 100%;
    max-width: 100%;
    max-height: ${({ level }) => (level == 'overview' ? '280px' : '240px')};
    margin-top: 12px;
    margin-right: 0px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    max-height: 240px;
  }
`;

const HeroImageBottomSection = styled.div`
  display: flex;
  max-width: ${({ level }) => (level === 'core' ? '1076px' : '680px')};
  align-items: flex-start;
  gap: 20px;
  margin-top: ${({ level }) => (level === 'detail' || level === 'press-and-media' ? '20px' : '')};

  @media screen and (max-width: ${media.mediumTop}) {
    max-width: 100%;
    width: 100%;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    margin: ${({ level }) => (level !== 'overview' ? '12px 0' : '')};
    gap: 12px;
  }
`;

const HeroImageBottom = styled(GatsbyImage)`
  width: ${({ level }) => (level === 'core' ? '100%' : '330px')};
  height: ${({ level }) => (level === 'core' ? '100%' : '200px')};

  @media screen and (max-width: ${media.mediumTop}) {
    aspect-ratio: 362 / 280;
    width: 100%;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    aspect-ratio: 225 / 160;
    min-height: 160px;
    min-width: 226px;
  }
`;

export default renderHeroSection;
